import {
  closestCenter,
  type CollisionDetection,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';

/**
 * Creates a collision detection algorithm for pipeline boards.
 *
 * Always returns column-level (stage) collision targets. Card-level collisions
 * are intentionally avoided because items are not transferred between
 * SortableContexts mid-drag — they only move after drop via an API call.
 * Returning card targets would cause dnd-kit's sortable logic to visually
 * displace other cards across columns.
 *
 * Resolution order:
 * 1. Pointer over a column → that column (handles cards and empty space)
 * 2. Dragged card still overlapping a column → that column, so a release whose
 *    pointer has drifted just outside a column the card visibly covers still
 *    reads as intentional
 * 3. Keyboard drag (no pointer) → closest column, keeping a11y working
 * 4. Anything else → NO target, so the drop is a no-op and the card snaps back
 *
 * Step 4 is the point of the whole thing: there must be no such thing as a drop
 * that lands somewhere the user did not aim. An earlier version fell back to
 * the closest column unconditionally, which made every release valid no matter
 * where it happened — releasing over the sidebar or between two columns quietly
 * moved the card to whichever stage was nearest.
 *
 * @param columnIds - Array of column droppable IDs (stage entity_ids)
 * @returns CollisionDetection function for use with DndContext
 */
export function createColumnFirstCollision(
  columnIds: string[]
): CollisionDetection {
  const columnIdSet = new Set(columnIds);

  return args => {
    // Check what the pointer is within (columns and/or cards)
    const pointerCollisions = pointerWithin(args);

    if (pointerCollisions.length > 0) {
      // Always prefer a column collision — even when the pointer is over a
      // card, return the parent column so dnd-kit doesn't trigger cross-
      // container sortable logic.
      const columnCollision = pointerCollisions.find(collision =>
        columnIdSet.has(String(collision.id))
      );

      if (columnCollision) {
        return [columnCollision];
      }
    }

    // Pointer is in the gap between columns or outside all droppables.
    // Use rectIntersection filtered to columns only to find the nearest column.
    // This prevents erratic behavior from closestCorners picking a distant card.
    const columnOnlyArgs = {
      ...args,
      droppableContainers: args.droppableContainers.filter(container =>
        columnIdSet.has(String(container.id))
      ),
    };

    const columnFallback = rectIntersection(columnOnlyArgs);
    if (columnFallback.length > 0) {
      return [columnFallback[0]];
    }

    // A keyboard drag has no pointer, so there is nothing for it to be
    // "outside" of — resolve to the closest column so keyboard drag-and-drop
    // keeps working. Removing this would break a11y silently: nothing throws,
    // the drop just stops landing anywhere.
    if (!args.pointerCoordinates) {
      const closestColumnResult = closestCenter(columnOnlyArgs);
      if (closestColumnResult.length > 0) {
        return [closestColumnResult[0]];
      }
    }

    // Pointer drag released somewhere that is not a column at all — the gap
    // between columns, the surrounding chrome, off-board entirely. Returning no
    // target makes the drop a no-op so the card snaps back to where it was
    // dragged from.
    //
    // This deliberately does NOT fall back to the nearest column. Doing so made
    // every release a valid drop no matter where it landed, so a card let go
    // over the sidebar or between two columns silently moved to whichever stage
    // happened to be closest — a stage the user never aimed at.
    return [];
  };
}
